import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateString } from '/imports/validators';

// Drawn map regions already probed for footprints, redrawn when the batch is reopened.
export interface BatchRectangle { north: number; south: number; east: number; west: number; }
export interface BatchCircle { center: { lat: number; lng: number }; radius: number; } // radius in meters
// A shape drawn with the map's measuring tool, redrawn with its length and area when the batch is reopened.
export interface BatchMeasurement { closed: boolean; path: { lat: number; lng: number }[]; }

export interface ProspectBatch extends Doc {
  orgId: string;
  name: string;
  enrichingProspectId?: string;
  enrichQueue?: string[];
  enrichQueueTotal?: number;
  useNearmap?: boolean;
  /** run the ICA enrichment: incentive zones, nearest substations and circuits, circuit load profiles */
  useIca?: boolean;
  rectangles?: BatchRectangle[];
  circles?: BatchCircle[];
  measurements?: BatchMeasurement[];
}
export type Model = ProspectBatch;

export const ProspectBatches = new Mongo.Collection<ProspectBatch>('prospectBatches');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: ProspectBatches,
  editableFields: ['active', 'orgId', 'name', 'enrichingProspectId', 'useNearmap', 'useIca', 'rectangles', 'circles', 'measurements'],
  fieldLimits: {
    'enrichingProspectId': 64, 'name': 256, 'orgId': 64,
  },
  isOrgOwned: true,
  listMethod: DeclareMethod<ListMethodArg<ProspectBatch>, ListMethodResponse<ProspectBatch, Refs>>('listProspectBatches'),
  getMethod: DeclareMethod<string, GetMethodResponse<ProspectBatch, Refs>>('getProspectBatch'),
  createMethod: DeclareMethod<Partial<ProspectBatch>, string>('createProspectBatch'),
  updateMethod: DeclareMethod<Partial<ProspectBatch>, string>('updateProspectBatch'),
  deleteMethod: DeclareMethod<string>('deleteProspectBatch'),
  pathPrefix: '/prospector',
  asString: (document) => document.name || document._id,
  modelName: 'Prospect Batch',
  pluralName: 'Prospect Batches',
  /**
   * Builds a Mongo query matching by id or name for the given search term.
   * @param search - search term
   * @returns Mongo selector
   */
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { name: new RegExp(search, 'gi') },
      ]
    };
  },
  /**
   * Validates required fields for a batch create/update.
   * @param batch - partial batch
   * @returns map of field name to error messages
   */
  validate: async (batch: Partial<ProspectBatch>) => {
    const errors: { [key: string]: string[] } = {};
    validateString(errors, batch, 'orgId', { required: true });
    validateString(errors, batch, 'name', { required: true });
    return errors;
  },
};

Meteor.isServer && Meteor.startup(async () => {
  await ProspectBatches.createIndexAsync({ orgId: 1 });
});
